import * as plugins from './typedserver_web.plugins.js';
import * as interfaces from '../dist_ts_interfaces/index.js';
import { logger } from './typedserver_web.logger.js';
logger.log('info', `TypedServer-Devtools initialized!`);

import { TypedserverStatusPill } from './typedserver_web.statuspill.js';

// Import hook types from typedrequest
type ITypedRequestLogEntry = plugins.typedrequest.ITypedRequestLogEntry;

export class ReloadChecker {
  public reloadJustified = false;
  public backendConnectionLost = false;
  public statusPill = new TypedserverStatusPill();
  public store = new plugins.webstore.WebStore({
    dbName: 'apiglobal__typedserver',
    storeName: 'apiglobal__typedserver',
  });
  public storeKey = 'lastServerChange';

  public typedsocket: plugins.typedsocket.TypedSocket;
  public typedrouter = new plugins.typedrequest.TypedRouter();
  private swStatusUnsubscribe: (() => void) | null = null;
  private trafficLoggingEnabled = false;

  constructor() {
    // Listen to browser online/offline events
    window.addEventListener('online', () => {
      this.statusPill.updateStatus({
        source: 'network',
        type: 'online',
        message: 'Back online',
        persist: false,
        timestamp: Date.now(),
      });
    });

    window.addEventListener('offline', () => {
      this.statusPill.updateStatus({
        source: 'network',
        type: 'offline',
        message: 'No internet connection',
        persist: true,
        timestamp: Date.now(),
      });
    });
  }

  public async reload() {
    // this looks a bit hacky, but apparently is the safest way to really reload stuff
    window.location.reload();
  }

  /**
   * Subscribe to service worker status updates
   */
  public subscribeToServiceWorker(): void {
    // Check if service worker client is available
    if (globalThis.globalSw?.actionManager) {
      this.swStatusUnsubscribe = globalThis.globalSw.actionManager.subscribeToStatusUpdates((status) => {
        this.statusPill.updateStatus({
          source: status.source,
          type: status.type,
          message: status.message,
          details: status.details,
          persist: status.persist || false,
          timestamp: status.timestamp,
        });
      });
      logger.log('info', 'Subscribed to service worker status updates');

      // Get initial SW status
      this.fetchServiceWorkerStatus();
    } else {
      logger.log('note', 'Service worker client not available yet, will retry...');
      // Retry after a delay
      setTimeout(() => this.subscribeToServiceWorker(), 2000);
    }
  }

  /**
   * Fetch and display initial service worker status
   */
  private async fetchServiceWorkerStatus(): Promise<void> {
    if (!globalThis.globalSw?.actionManager) return;

    try {
      const status = await globalThis.globalSw.actionManager.getServiceWorkerStatus();
      if (status) {
        this.statusPill.updateStatus({
          source: 'serviceworker',
          type: status.isActive ? 'connected' : 'disconnected',
          message: status.isActive ? 'Service worker active' : 'Service worker inactive',
          details: {
            cacheHitRate: status.cacheHitRate,
            resourceCount: status.resourceCount,
            connectionType: status.connectionType,
          },
          persist: false,
          timestamp: Date.now(),
        });
      }
    } catch (error) {
      logger.log('warn', `Failed to get SW status: ${error}`);
    }
  }

  /**
   * starts the reload checker
   */
  public async performHttpRequest() {
    logger.log('info', 'performing http check...');
    (await this.store.get(this.storeKey))
      ? null
      : await this.store.set(this.storeKey, globalThis.typedserver.lastReload);

    let response: Response;

    try {
      const controller = new AbortController();
      plugins.smartdelay.delayFor(5000).then(() => {
        controller.abort();
      });
      response = await fetch('/typedserver/reloadcheck', {
        method: 'POST',
        signal: controller.signal,
      });
    } catch (err: any) {}

    if (response?.status !== 200) {
      this.backendConnectionLost = true;
      logger.log('warn', `got a status ${response?.status}.`);
      this.statusPill.updateStatus({
        source: 'backend',
        type: 'disconnected',
        message: `Backend connection lost (${response?.status || 'timeout'})`,
        persist: true,
        timestamp: Date.now(),
      });
    }
    if (response?.status === 200 && this.backendConnectionLost) {
      this.backendConnectionLost = false;
      this.statusPill.updateStatus({
        source: 'backend',
        type: 'connected',
        message: 'Backend connection restored',
        persist: false,
        timestamp: Date.now(),
      });
    }
    return response;
  }

  public async checkReload(lastServerChange: number) {
    let reloadJustified = false;
    let storedLastServerChange = await this.store.get(this.storeKey);
    if (storedLastServerChange && storedLastServerChange !== lastServerChange) {
      reloadJustified = true;
    } else {
    }

    if (reloadJustified) {
      this.store.set(this.storeKey, lastServerChange);
      const hasSw = !!globalThis.globalSw;
      this.statusPill.updateStatus({
        source: 'serviceworker',
        type: 'update',
        message: hasSw ? 'Updating app...' : 'Upgrading...',
        persist: true,
        timestamp: Date.now(),
      });

      if (globalThis.globalSw?.purgeCache) {
        await globalThis.globalSw.purgeCache();
      } else if ('caches' in window) {
        // Fallback: clear caches via Cache API when service worker client isn't initialized
        try {
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map(key => caches.delete(key)));
          logger.log('ok', 'Cleared caches via Cache API fallback');
        } catch (err) {
          logger.log('warn', `Failed to clear caches via Cache API: ${err}`);
        }
      } else {
        console.log('globalThis.globalSw not found and Cache API not available...');
      }

      this.statusPill.updateStatus({
        source: 'serviceworker',
        type: 'cache',
        message: 'Cache cleared, reloading...',
        persist: true,
        timestamp: Date.now(),
      });
      await plugins.smartdelay.delayFor(200);
      this.reload();
      return;
    } else {
      // All good, hide after brief show
      return;
    }
  }

  public async connectTypedsocket() {
    if (!this.typedsocket) {
      this.typedrouter.addTypedHandler<interfaces.IReq_PushLatestServerChangeTime>(
        new plugins.typedrequest.TypedHandler('pushLatestServerChangeTime', async (dataArg) => {
          this.checkReload(dataArg.time);
          return {};
        })
      );
      this.typedsocket = await plugins.typedsocket.TypedSocket.createClient(
        this.typedrouter,
        plugins.typedsocket.TypedSocket.useWindowLocationOriginUrl()
      );
      await this.typedsocket.setTag('typedserver_frontend', {});
      this.typedsocket.statusSubject.subscribe(async (statusArg) => {
        console.log(`typedsocket status: ${statusArg}`);
        if (statusArg === 'disconnected' || statusArg === 'reconnecting') {
          this.backendConnectionLost = true;
          this.statusPill.updateStatus({
            source: 'backend',
            type: statusArg === 'disconnected' ? 'disconnected' : 'reconnecting',
            message: `TypedSocket ${statusArg}`,
            persist: true,
            timestamp: Date.now(),
          });
        } else if (statusArg === 'connected' && this.backendConnectionLost) {
          this.backendConnectionLost = false;
          this.statusPill.updateStatus({
            source: 'backend',
            type: 'connected',
            message: 'TypedSocket connected',
            persist: false,
            timestamp: Date.now(),
          });
          // lets check if a reload is necessary
          const getLatestServerChangeTime =
            this.typedsocket.createTypedRequest<interfaces.IReq_GetLatestServerChangeTime>(
              'getLatestServerChangeTime'
            );
          const response = await getLatestServerChangeTime.fire({});
          this.checkReload(response.time);
        }
      });
      logger.log('success', `ReloadChecker connected through typedsocket!`);

      // Enable traffic logging for sw-dash
      this.enableTrafficLogging();
    }
  }

  public started = false;
  public async start() {
    this.started = true;
    logger.log('info', `starting ReloadChecker...`);

    // Subscribe to service worker status updates
    this.subscribeToServiceWorker();

    while (this.started) {
      const response = await this.performHttpRequest();
      if (response?.status === 200) {
        logger.log('info', `ReloadChecker reached backend!`);
        await this.checkReload(parseInt(await response.text()));
        await this.connectTypedsocket();
      }
      await plugins.smartdelay.delayFor(120000);
    }
  }

  public async stop() {
    this.started = false;
    if (this.swStatusUnsubscribe) {
      this.swStatusUnsubscribe();
      this.swStatusUnsubscribe = null;
    }
    // Clear global hooks when stopping
    if (this.trafficLoggingEnabled) {
      plugins.typedrequest.TypedRouter.clearGlobalHooks();
      this.trafficLoggingEnabled = false;
    }
  }

  /**
   * Enable TypedRequest traffic logging to the service worker
   * Sets up global hooks on TypedRouter to capture all request/response traffic
   */
  public enableTrafficLogging(): void {
    if (this.trafficLoggingEnabled) {
      logger.log('note', 'Traffic logging already enabled');
      return;
    }

    // Check if service worker client is available
    if (!globalThis.globalSw?.actionManager) {
      logger.log('note', 'Service worker client not available, will retry traffic logging setup...');
      setTimeout(() => this.enableTrafficLogging(), 2000);
      return;
    }

    const actionManager = globalThis.globalSw.actionManager;

    // Helper function to log entries
    const logEntry = (entry: ITypedRequestLogEntry) => {
      // Skip logging serviceworker_* methods to avoid infinite loops
      // These are internal SW communication methods, not app traffic
      if (entry.method.startsWith('serviceworker_')) {
        return;
      }
      actionManager.logTypedRequest(entry);
    };

    // Set up global hooks on TypedRouter
    plugins.typedrequest.TypedRouter.setGlobalHooks({
      onOutgoingRequest: logEntry,
      onIncomingResponse: logEntry,
      onIncomingRequest: logEntry,
      onOutgoingResponse: logEntry,
    });

    this.trafficLoggingEnabled = true;
    logger.log('success', 'TypedRequest traffic logging enabled');
  }
}

const reloadCheckInstance = new ReloadChecker();
reloadCheckInstance.start();
